import { useCallback, useEffect, useState, Fragment, ChangeEvent, FC } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
// Hooks
import useDialog from 'hooks/useDialog';
import { useAppDispatch } from 'hooks/redux';
// Async
import PropertyOwnershipsAsync from 'store/propertyOwnerships/propertyOwnershipsAsync';
import PropertiesAsync from 'store/properties/propertiesAsync';
// Components
import PropertiesFormDialog from 'components/PropertiesForm.dialog';
// Types
import ViewTypes from 'types/ViewTypes';
import PropertyOwnershipStatus from 'types/PropertyOwnershipStatus';
// Models
import IPropertyOwnership from 'models/PropertyOwnership';
import IProperty from 'models/Property';
// Actions
import { PropertyOwnershipsActions } from 'store/propertyOwnerships/propertyOwnershipsSlice';
import { UiActions } from 'store/ui/uiSlice';
import { PropertiesActions } from 'store/properties/propertiesSlice';
// Selectors
import { selectFormData } from 'store/propertyOwnerships/propertyOwnershipsSelectors';
import { selectAllProperties } from 'store/properties/propertiesSelectors';
// Mui
import {
  Button, DialogActions, DialogContent,
  DialogTitle, TextField, Autocomplete,
  Grid, FormControl, InputLabel, Select,
  MenuItem, Box, debounce, FormHelperText,
  IconButton
} from '@mui/material';
import {
  LoadingButton,
  MobileDatePicker
} from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
// Utilities
import { isFieldRequired } from 'utilities/Validation';
import { getContent } from "utilities/Utilities";

interface IFormData {
  start: any,
  end: any,
  view: string,
  bedrooms: string | number,
  bathrooms: string | number,
  price: string | number,
  ownerNotes: string,
  status: PropertyOwnershipStatus | null,
}

type Props = {
  onClose: () => void;
  propertyOwnership?: IPropertyOwnership | null;
}

const MyPropertyOwnershipsForm:FC<Props> = ({
  // Props
  onClose, propertyOwnership
}) => {
  const propertyOwnershipId:number | null = propertyOwnership?.id || null;
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const properties: IProperty[] = useSelector(selectAllProperties) || [];
  const formData = useSelector(selectFormData);

  const [selectedProperty, setSelectedProperty] = useState(formData.property);
  const [selectedPropertyError, setSelectedPropertyError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingProperties, setIsLoadingProperties] = useState<boolean>(false);

  const handleSelectedProperty = (property: IProperty | null) => {
    setSelectedProperty(property);
    setSelectedPropertyError(property ? false : true);
  }

  useEffect(() => {
    setSelectedProperty(formData.property);
  }, [formData]);

  const { control, handleSubmit, formState:{ errors }, setValue, watch, setError } = useForm<IFormData>({
    defaultValues: {
      start: propertyOwnership?.start || '',
      end: propertyOwnership?.end || '',
      view: propertyOwnership?.view || '',
      bedrooms: propertyOwnership?.bedrooms || '',
      bathrooms: propertyOwnership?.bathrooms || '',
      price: propertyOwnership?.price || '',
      ownerNotes: propertyOwnership?.ownerNotes || '',
    }
  });

  const startDate = watch('start');
  const endDate = watch('end');

  const onSubmit = handleSubmit((data:IFormData) => {
    if (!startDate && endDate) {
      setError('start', { type: 'required', message: 'Either both or none dates should be selected' }, { shouldFocus: true });
      return;
    }

    if (startDate && !endDate) {
      setError('end', { type: 'required', message: 'Either both or none dates should be selected' }, { shouldFocus: true });
      return;
    }

    if (!selectedProperty && !propertyOwnershipId) {
      setSelectedPropertyError(true);
      return;
    }

    const { start, end, price } = data;
    const nextData = {
      ...data,
      start: start ? dayjs(start).format('YYYY-MM-DD') : null,
      end: start ? dayjs(end).format('YYYY-MM-DD') : null,
      price: price ? +price : null,
    }

    setIsLoading(true);

    if (propertyOwnershipId) {
      const newData = { id: propertyOwnershipId, ...nextData };
      dispatch(PropertyOwnershipsAsync.updatePropertyOwnerships(newData))
        .unwrap()
        .then(() => onClose())
        .finally(() => setIsLoading(false));
    } else {
      const newData = { propertyId: selectedProperty?.id, ...nextData };
      dispatch(PropertyOwnershipsAsync.createPropertyOwnerships(newData))
        .unwrap()
        .then(() => {
          dispatch(UiActions.enqueueSnackbar({ message: `${getContent('labels').labelTimeshareSingularText} was created` }));
        })
        .then(() => onClose())
        .finally(() => setIsLoading(false));
    }
  });

  useEffect(() => {
    return () => {
      dispatch(PropertyOwnershipsActions.setFormData(null));
    }
    // eslint-disable-next-line
  }, []);

  const { Dialog, openDialog, closeDialog } = useDialog();

  // eslint-disable-next-line
  const debounceProperty = useCallback(debounce((search:string) => {
    if (search.trim()) {
      setIsLoadingProperties(true);
      dispatch(PropertiesAsync.fetchProperties({ search, size: 50 }))
        .unwrap()
        .finally(() => setIsLoadingProperties(false));
    } else {
      dispatch(PropertiesActions.clearProperties());
    }
  }, 500), []);

  const onChangeProperty = (event:ChangeEvent<HTMLInputElement>) => {
    debounceProperty(event.target.value);
  }

  return (
    <Fragment>
      <DialogTitle>{`${propertyOwnershipId ? 'Edit' : 'Create'} ${getContent('labels').labelTimeshareSingularText}`}</DialogTitle>
      <DialogContent dividers>
        <form onSubmit={onSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {/* Start date */}
              <Controller
                control={control} name="start"
                render={({ field: { onChange, value } }) => (
                  <MobileDatePicker
                    clearable
                    value={value || null}
                    minDate={dayjs().subtract(10, 'year')}
                    maxDate={endDate ? dayjs(endDate).subtract(1, 'day') : dayjs().add(10, 'year')}
                    onChange={(date:any) => {
                      onChange(date);
                      if (date && !endDate) {
                        setValue('end', dayjs(date).add(7, 'day'));
                      }
                    }}
                    label="Start date"
                    renderInput={(params) => <TextField {...params} fullWidth margin="normal" error={Boolean(errors.start)} helperText={errors.start ? errors.start.message : ''} />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {/* End date */}
              <Controller
                control={control} name="end"
                render={({ field: { onChange, value } }) => (
                  <MobileDatePicker
                    clearable
                    value={value || null}
                    maxDate={dayjs().add(10, 'year')}
                    minDate={startDate ? dayjs(startDate).add(1, 'day') : dayjs().subtract(10, 'year')}
                    onChange={(date:any) => {
                      onChange(date);
                      if (date && !startDate) {
                        setValue('start', dayjs(date).subtract(7, 'day'));
                      }
                    }}
                    label="End date"
                    renderInput={(params) => <TextField {...params} fullWidth margin="normal" error={Boolean(errors.end)} helperText={errors.end ? errors.end.message : ''} />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Amount bedrooms */}
              <Controller
                control={control} name="bedrooms"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="Bedrooms-select-label">Bedrooms</InputLabel>
                    <Select
                      {...field}
                      labelId="Bedrooms-select-label"
                      id="Bedrooms-select"
                      label="Bedrooms"
                      name="bedrooms"
                    >
                      <MenuItem value="">Unknown</MenuItem>
                      <MenuItem value="studio">Studio</MenuItem>
                      <MenuItem value="1">1</MenuItem>
                      <MenuItem value="2">2</MenuItem>
                      <MenuItem value="3">3</MenuItem>
                      <MenuItem value="4">4</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Amount bathrooms */}
              <Controller
                control={control} name="bathrooms"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="Bathrooms-select-label">Bathrooms</InputLabel>
                    <Select
                      {...field}
                      labelId="Bathrooms-select-label"
                      id="Bathrooms-select"
                      label="Bathrooms"
                      name="bathrooms"
                    >
                      <MenuItem value="">Unknown</MenuItem>
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                      <MenuItem value={3}>3</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {/* View */}
              <Controller
                control={control} name="view"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="View"
                    select
                    fullWidth
                    error={Boolean(errors.view)}
                    helperText={errors.view ? errors.view.message : ''}
                    required
                  >
                    {(Object.keys(ViewTypes) as Array<keyof typeof ViewTypes>).map((key) => (
                      <MenuItem key={key} value={ViewTypes[key]}>{ViewTypes[key]}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Price */}
              <Controller
                control={control} name="price"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    onChange={(e) => {field.onChange(e.target.value.replace(/\D/, '').slice(0,6))}}
                    label="Price"
                    type="text"
                  />
                )}
              />
            </Grid>

            {!propertyOwnershipId && (
              <Fragment>
                <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center', mt: 2, width: '100%' }}>
                  <Autocomplete
                    sx={{ flexGrow: 1, ml: 2 }}
                    disablePortal
                    id="my-property-ownership-select-propertyId"
                    options={properties}
                    value={selectedProperty}
                    getOptionLabel={(property) => property.name}
                    onChange={(_:any, value:IProperty | null) => handleSelectedProperty(value || null)}
                    onClose={() => debounceProperty('')}
                    loading={isLoadingProperties}
                    loadingText="Search..."
                    noOptionsText=""
                    forcePopupIcon={false}
                    filterOptions={options => options}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => 
                      <TextField
                        {...params}
                        label="Property"
                        fullWidth
                        required
                        error={selectedPropertyError}
                        helperText={selectedPropertyError ? 'This field is required' : ''}
                        onChange={onChangeProperty}
                        placeholder="e.g. Marriott"
                      />
                    }
                  />
                  <Button
                    startIcon={<AddIcon />}
                    sx={{ height: '56px', width: 'max-content', display: { xs: 'none', md: 'inline-flex' } }}
                    onClick={openDialog}
                  >
                    Create property
                  </Button>
                  <IconButton
                    sx={{ height: '56px', display: { md: 'none' }, color: getContent('theme').secondaryColor }}
                    onClick={openDialog}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
                <FormHelperText sx={{ ml: 2, mt: 0.5 }}>
                  Start typing to find your property. If not found then create a new property.
                </FormHelperText>
              </Fragment>
            )}

            <Grid item xs={12}>
              {/* ownerNotes */}
              <Controller
                control={control} name="ownerNotes"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Notes"
                    type="text"
                    multiline
                    rows={3}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ width: '100%' }}>
        <Button
          variant="text"
          color="primary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          loading={isLoading}
          onClick={onSubmit}
          variant="contained"
          color="primary"
        >
          {propertyOwnershipId ? 'Save' : 'Create' }
        </LoadingButton>
      </DialogActions>
      <Dialog maxWidth="sm">
        <PropertiesFormDialog onClose={closeDialog} />
      </Dialog>
    </Fragment>
  )
}

export default MyPropertyOwnershipsForm;
